import { useEffect, useState } from "react";
import { useRequestFinished } from "../../../common/hooks";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TabMenu } from "primereact/tabmenu";
import CustomBreadCrumbs from "../../../common/breadcrumbs";
import Form from "../../../common/form";
import {
  resetUpload,
  uploadRequest,
} from "../../../../store/slices/uploadSlice";
import {
  createRequest,
  detailsRequest,
  updateDetailRequest,
} from "../../../../store/slices/learningMilestoneSlice";
import { listRequest } from "../../../../store/slices/learningObjectsSlice";
import Section from "../../../common/section";
import FileUpload from "../../../common/fileUpload";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import TooltipButton from "../../../common/tooltipButton";
import { AccessScopeConstants, GlobalConstants } from "../../../../constants";
import LearningMilestoneDetailsForm from "./LearningMilestoneDetailsForm";
import List from "../../../common/list";
import { useMountEffect } from "primereact/hooks";
import {
  PICKER_PAGE_SIZE,
  useServerSearch,
} from "../../../common/hooks/useServerSearch";

const LearningMilestoneDetails = ({
  labels,
  menu,
  resetUpload,
  uploadDetails,
  uploading,
  progress,
  uploadRequest,
  createRequest,
  action,
  error,
  requesting,
  detailsRequest,
  record,
  user,
  updateDetailRequest,
  listRequest,
  learningObjects,
}) => {
  const params = useParams();
  const [bannerFileName, setBannerFileName] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const [isEditable, setIsEditable] = useState(params?.id ? false : true);
  const [activeTab, setActiveTab] = useState(0);
  const [isRecordClone, setIsRecordClone] = useState(false);

  const { role } = user || {};
  const canUpdate = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.ContentAccess,
    AccessScopeConstants.UpdateScope
  );

  // The learning object picker: the first page by name, then the server's
  // matches for what the admin types (E2E25)
  const loadLearningObjects = (namePattern = "") =>
    listRequest({
      pageSize: PICKER_PAGE_SIZE,
      pageNo: 1,
      sortBy: "asc",
      sortColumn: "name",
      namePattern,
    });
  const searchLearningObjects = useServerSearch(loadLearningObjects);

  useMountEffect(() => {
    resetUpload();
    loadLearningObjects();

    const searchParams = new URLSearchParams(location?.search);
    const cloneId = searchParams.get("clone");
    if (cloneId) {
      setIsRecordClone(true);
      detailsRequest({ id: cloneId });
    }
  });

  useEffect(() => {
    if (params?.id) {
      detailsRequest({ id: params.id });
    }
  }, [params?.id, detailsRequest]);

  // Only a save answered while this screen is open: the persisted store can
  // still say "update" from an earlier save when the screen mounts (F-BUG-12).
  const requestFinished = useRequestFinished(requesting);
  useEffect(() => {
    if ((action === "create" || action === "update") && requestFinished && !error) {
      navigate(menu.to);
    }
  }, [error, action, requestFinished, menu, navigate]);

  const onBannerUpload = (event) => {
    const file = event.files[0];
    setBannerFileName(file.name);
    uploadRequest({ file, type: "image" });
  };

  const onSubmit = (values) => {
    const payload = {
      ...values,
      name: values?.name,
      tags: values?.tags || null,
      isCertified: false,
      bannerImageId:
        uploadDetails?.[bannerFileName]?.artifactId ||
        record?.bannerImageId ||
        null,
      objects: values?.objects?.map((item, index) => ({
        operator: item?.operator?.id,
        learningObjectId: item?.learningObject?.id,
        order: index + 1,
      })),
      minimumLearningObjectsForCompletion: values?.objects?.filter(
        (item) =>
          item.operator.id ===
          GlobalConstants.LEARNING_OPERATORS.find((x) => x.default).id
      ).length,
    };
    if (params?.id) {
      delete payload.objects;
      delete payload.minimumLearningObjectsForCompletion;
      updateDetailRequest({ id: params.id, payload });
    } else {
      createRequest(payload);
    }
  };

  return (
    <div className="pb-8 md:pb-0">
      <CustomBreadCrumbs
        labels={labels}
        navigate={navigate}
        editLabel={record?.name}
      />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({
              id: params?.id
                ? "edit_learning_milestone"
                : "add_learning_milestone",
            })}
          </div>
          {(params?.id && !isEditable && activeTab === 0 && (
            <div className="edexlms-page-actions">
              <TooltipButton
                icon="pi pi-pencil"
                outlined
                onClick={() =>
                  (record?.id && setIsEditable(!isEditable)) || null
                }
                label={intl.formatMessage({ id: "edit" })}
                type="button"
                size="small"
                disabled={!canUpdate || !record?.id}
                tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
                tooltip={
                  canUpdate && !record?.isSystemDefined
                    ? ""
                    : intl.formatMessage({ id: "no_permission" })
                }
              />
            </div>
          )) ||
            null}
        </div>
        <div className="edexlms-page-body learning-milestones">
          {(params?.id && (
            <TabMenu
              className="edexlms-tab-menu"
              model={[
                {
                  label: intl.formatMessage({ id: "basic_info" }),
                  command: () => {
                    if (activeTab !== 0) {
                      setActiveTab(0);
                      setIsEditable(false);
                    }
                  },
                },
                {
                  label: intl.formatMessage({ id: "learning_objects" }),
                  command: (e) => {
                    if (activeTab !== 1) {
                      setActiveTab(1);
                      setIsEditable(false);
                    }
                  },
                },
              ]}
            />
          )) ||
            null}
          {(activeTab === 0 && (
            <Form
              initialValues={{
                name: (record && !isRecordClone && record?.name) || "",
                tags: record?.tags,
                isCertified: record?.isCertified || false,
                isLinear: record?.isLinear || false,
                objects: ((!record || !isRecordClone) && [
                  {
                    operator: GlobalConstants.LEARNING_OPERATORS.filter(
                      (op) => op.default
                    ).map((item) => ({
                      name: intl.formatMessage({ id: item.name }),
                      id: item.id,
                    }))[0],
                  },
                ]) || [
                  ...record?.objects.map((item) => ({
                    operator: GlobalConstants.LEARNING_OPERATORS.filter(
                      (op) => op.name === item.operator
                    ).map((op) => ({
                      name: intl.formatMessage({ id: op.name }),
                      id: op.id,
                    }))[0],
                    // From the milestone itself, not the picker's current
                    // list, which a search replaces
                    learningObject: {
                      name: item.learningObjectType
                        ? `${item.learningObjectName} (${item.learningObjectType})`
                        : item.learningObjectName,
                      id: item.learningObjectId,
                    },
                  })),
                ],
              }}
              disabled={!isEditable}
              submitBtn={{
                label: intl.formatMessage({
                  id: params?.id
                    ? "update_learning_milestone"
                    : "add_learning_milestone",
                }),
                onClick: onSubmit,
              }}
              cancelBtn={{
                label: intl.formatMessage({ id: "cancel" }),
                onClick: () => navigate(menu?.to),
              }}
            >
              <div className="flex flex-column gap-4">
                <LearningMilestoneDetailsForm
                  learningObjects={learningObjects}
                  onObjectSearch={searchLearningObjects}
                  disabled={!isEditable}
                  readOnly={params?.id ? true : false}
                  recordId={params?.id || null}
                />
              </div>
            </Form>
          )) ||
            null}
          {(activeTab === 1 && (
            <List
              selectionMode=""
              dataKey="learningObjectId"
              data={record?.objects || []}
              columns={[
                {
                  field: "learningObject",
                  header: "learning_object",
                  sortable: false,
                  body: (rowData) => rowData?.learningObjectName || "",
                },
                {
                  field: "operator",
                  header: "learning_object_complusion",
                  sortable: false,
                  body: (rowData) => {
                    return (
                      (rowData.order > 1 &&
                        intl.formatMessage({
                          id: rowData?.operator,
                        })) ||
                      "-"
                    );
                  },
                },
              ]}
              pagination={false}
            />
          )) ||
            null}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  action: state.learningMilestones?.action,
  error: state.learningMilestones?.error,
  progress: state.uploads?.progress,
  record: state.learningMilestones?.record,
  requesting: state.learningMilestones?.requesting,
  uploadDetails: state.uploads?.details,
  uploading: state.uploads?.uploading,
  user: state.self?.user,
  learningObjects: state?.learningObjects?.data?.list || [],
});

const mapDispatchToProps = (dispatch) => ({
  resetUpload: () => dispatch(resetUpload()),
  uploadRequest: (data) => dispatch(uploadRequest(data)),
  createRequest: (data) => dispatch(createRequest(data)),
  detailsRequest: (id) => dispatch(detailsRequest(id)),
  updateDetailRequest: (data) => dispatch(updateDetailRequest(data)),
  listRequest: (data) => dispatch(listRequest(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LearningMilestoneDetails);
